import type {BolSalesChannelWithStatusDelegationIssued} from '../../types/salesChannels/bolSalesChannels';
import type {EcommerceSalesChannel} from '@/types/salesChannels/index';
import type {ShopifySalesChannelWithStatusDelegationIssued} from '@/types/salesChannels/shopifySalesChannels';
import type {WoocommerceSalesChannelWithStatusDelegationIssued} from '@/types/salesChannels/woocommerceSalesChannels';
import type {
  SalesChannelWithStatusDelegationIssued,
  SalesChannelWithStatusDelegationRequested,
  SalesChannelWithStatusDelegationRequired,
} from '@myparcel-frontend/bol-ecommerce-service';
import type {
  SalesChannelStatusDelegationIssued,
  SalesChannelStatusDelegationRequested,
  SalesChannelStatusDelegationRequired,
} from '@myparcel-frontend/event-service';
import {merge} from 'es-toolkit';
import {defineStore} from 'pinia';
import {ref} from 'vue';
import {SalesChannelsStatus} from '@/types/salesChannels/index';

export const useSalesChannelStore = defineStore('salesChannels', () => {
  const salesChannelList = ref<EcommerceSalesChannel[]>([]);

  const addToSalesChannelList = (newSalesChannels: EcommerceSalesChannel): EcommerceSalesChannel[] => {
    salesChannelList.value = [...salesChannelList.value, newSalesChannels];
    return salesChannelList.value;
  };

  const removeFromSalesChannelList = (salesChannelsToRemove: EcommerceSalesChannel) => {
    salesChannelList.value = salesChannelList.value.filter((salesChannel) => {
      return salesChannelsToRemove.id !== salesChannel.id;
    });
  };

  const updateInSalesChannelList = (newSalesChannel: EcommerceSalesChannel) => {
    salesChannelList.value = salesChannelList.value.map((salesChannel) =>
      salesChannel.id === newSalesChannel.id ? newSalesChannel : salesChannel,
    );
  };

  const addSalesChannel = (salesChannel: EcommerceSalesChannel): EcommerceSalesChannel | undefined => {
    if (hasSalesChannelById(salesChannel.id)) {
      return undefined;
    }

    addToSalesChannelList(salesChannel);
    return salesChannel;
  };

  const hasSalesChannelById = (id: EcommerceSalesChannel['id']): boolean => {
    return salesChannelList.value.some((channel) => channel.id === id);
  };

  const getSalesChannelById = (id: EcommerceSalesChannel['id']): EcommerceSalesChannel | undefined => {
    return salesChannelList.value.find((channel) => channel.id === id);
  };

  const updateSalesChannel = (salesChannel: EcommerceSalesChannel): EcommerceSalesChannel | undefined => {
    updateInSalesChannelList(salesChannel);

    return getSalesChannelById(salesChannel.id);
  };

  const removeSalesChannel = (id: EcommerceSalesChannel['id']): boolean => {
    const existingChannel = getSalesChannelById(id);

    if (!existingChannel) {
      return false;
    }

    removeFromSalesChannelList(existingChannel);

    return true;
  };

  const enableOrderLoading = (
    salesChannelId: BolSalesChannelWithStatusDelegationIssued['id'],
  ):
    | BolSalesChannelWithStatusDelegationIssued
    | ShopifySalesChannelWithStatusDelegationIssued
    | WoocommerceSalesChannelWithStatusDelegationIssued
    | undefined => {
    const salesChannel = getSalesChannelById(salesChannelId);

    if (!salesChannel) {
      return undefined;
    }

    if (salesChannel.status !== SalesChannelsStatus.DelegationIssued) {
      return undefined;
    }

    salesChannel.extraction.schedule.transformAndLoad = true;

    return salesChannel;
  };

  const disableOrderLoading = (
    salesChannelId: BolSalesChannelWithStatusDelegationIssued['id'],
  ):
    | BolSalesChannelWithStatusDelegationIssued
    | ShopifySalesChannelWithStatusDelegationIssued
    | WoocommerceSalesChannelWithStatusDelegationIssued
    | undefined => {
    const salesChannel = getSalesChannelById(salesChannelId);

    if (!salesChannel) {
      return undefined;
    }

    if (salesChannel.status !== SalesChannelsStatus.DelegationIssued) {
      return undefined;
    }

    salesChannel.extraction.schedule.transformAndLoad = false;

    return salesChannel;
  };

  const updateSalesChannelToDelegationIssued = (
    salesChannelId: EcommerceSalesChannel['id'],
    newSalesChannelValues: SalesChannelStatusDelegationIssued['value'],
  ): EcommerceSalesChannel | undefined => {
    const salesChannel = getSalesChannelById(salesChannelId);

    if (!salesChannel) {
      return;
    }

    let baseSalesChannel;

    switch (salesChannel.status) {
      case SalesChannelsStatus.DelegationRequested: {
        const {authorizeUrl, ...rest} = salesChannel;
        baseSalesChannel = rest;
        break;
      }
      case SalesChannelsStatus.DelegationRequired: {
        const {delegationError, ...rest} = salesChannel;
        baseSalesChannel = rest;
        break;
      }
      default:
        baseSalesChannel = salesChannel;
        break;
    }

    const updatedSalesChannel = merge(
      {
        ...baseSalesChannel,
        status: SalesChannelsStatus.DelegationIssued,
      },
      newSalesChannelValues,
    );

    return updateSalesChannel(updatedSalesChannel);
  };

  const updateSalesChannelToDelegationRequested = (
    salesChannelId: EcommerceSalesChannel['id'],
    newSalesChannelValues: SalesChannelStatusDelegationRequested['value'],
  ): EcommerceSalesChannel | undefined => {
    const salesChannel = getSalesChannelById(salesChannelId);
    let updatedSalesChannel: SalesChannelWithStatusDelegationRequested;

    if (!salesChannel) {
      return;
    }

    switch (salesChannel.status) {
      case SalesChannelsStatus.DelegationRequired:
        const {delegationError, ...restOfSalesChannelWithRequiredDelegation} = salesChannel;

        updatedSalesChannel = {
          ...restOfSalesChannelWithRequiredDelegation,
          status: SalesChannelsStatus.DelegationRequested,
          authorizeUrl: newSalesChannelValues.authorizeUrl,
        };
        break;
      default:
        updatedSalesChannel = {
          ...salesChannel,
          status: SalesChannelsStatus.DelegationRequested,
          authorizeUrl: newSalesChannelValues.authorizeUrl,
        };
        break;
    }

    return updateSalesChannel(updatedSalesChannel);
  };

  const updateSalesChannelToDelegationRequired = (
    salesChannelId: EcommerceSalesChannel['id'],
    newSalesChannelValues: SalesChannelStatusDelegationRequired['value'],
  ): EcommerceSalesChannel | undefined => {
    let updatedSalesChannel: SalesChannelWithStatusDelegationRequired;
    const salesChannel = getSalesChannelById(salesChannelId);

    if (!salesChannel) {
      return;
    }

    switch (salesChannel.status) {
      case SalesChannelsStatus.DelegationRequested:
        const {authorizeUrl, ...restOfSalesChannelWithRequestedDelegation} = salesChannel;

        updatedSalesChannel = {
          ...restOfSalesChannelWithRequestedDelegation,
          status: SalesChannelsStatus.DelegationRequired,
          delegationError: newSalesChannelValues.delegationError,
        };
        break;
      default:
        updatedSalesChannel = {
          ...salesChannel,
          status: SalesChannelsStatus.DelegationRequired,
          delegationError: newSalesChannelValues.delegationError,
        };
        break;
    }

    return updateSalesChannel(updatedSalesChannel);
  };

  return {
    addSalesChannel,
    disableOrderLoading,
    enableOrderLoading,
    getSalesChannelById,
    hasSalesChannelById,
    removeSalesChannel,
    salesChannelList,
    updateSalesChannel,
    updateSalesChannelToDelegationIssued,
    updateSalesChannelToDelegationRequested,
    updateSalesChannelToDelegationRequired,
  };
});
